import type { ChatMessageContextItem } from '@/shared/generated'
import type { Action, Message } from '../model/types'

import { useCallback, useMemo, useReducer } from 'react'
import { useLocation, useParams } from '@tanstack/react-router'

import {
	useAssistantChatSettingsQuery,
	useSentChatMessageMutation,
	useSentChatMessageReactionMutation,
} from '@/entities/ai-assistant'

import { useOptimizationGroupId } from '@/shared/hooks/useOptimzationGroupId'
import { uuid } from '@/shared/tools/utils'

import { AssistantContext } from '../model/AssistantContext'

export default function AssistantProvider({
	children,
}: {
	children: React.ReactNode
}) {
	const settingsQuery = useAssistantChatSettingsQuery()
	const sendMutation = useSentChatMessageMutation()
	const reactionMutation = useSentChatMessageReactionMutation()
	const optimizationGroupId = useOptimizationGroupId()
	const location = useLocation()
	const params = useParams({
		strict: false,
	})
	const context: ChatMessageContextItem = useMemo(
		() => ({
			optimization_group_id: optimizationGroupId || null,
			page: getRoutePath(location as any, params),
		}),
		[optimizationGroupId, location, params]
	)

	const [state, dispatch] = useReducer(reducer, {
		agent_url: undefined,
		messages: [],
		session_id: uuid(),
	})
	const resetConversation = useCallback(() => {
		dispatch({ type: 'reset' })
	}, [dispatch])
	const setAgent = useCallback(
		(url: string) => {
			dispatch({ type: 'set_agent', url })
		},
		[dispatch]
	)
	const messageReaction = useCallback(
		(id: string | number, reaction: 'like' | 'dislike') => {
			reactionMutation.mutate({
				id: id.toString(),
				reaction,
			})
			dispatch({ type: 'reaction', id, reaction })
		},
		[reactionMutation, dispatch]
	)

	const sendMessage = useCallback(
		async (text: string) => {
			dispatch({
				type: 'add',
				message: {
					id: uuid(),
					sender: 'user',
					text,
				},
			})
			try {
				const answer = await sendMutation.mutateAsync({
					path: state.agent_url!,
					payload: {
						text,
						session_id: state.session_id,
						context,
					},
				})
				dispatch({
					type: 'add',
					message: {
						sender: 'assistant',
						reaction: undefined,
						...answer,
					},
				})
			} catch (error) {
				dispatch({
					type: 'add',
					message: {
						sender: 'assistant',
						is_system: true,
						reaction: undefined,
						id: uuid(),
						text: 'Sorry, I can`t proceed your request.',
					},
				})
			}
		},
		[dispatch, sendMutation, state.agent_url, state.session_id, context]
	)
	const agent = useMemo(() => {
		if (state.agent_url) {
			return settingsQuery.data?.agents.find(
				(agent) => agent.uri === state.agent_url
			)
		}

		return undefined
	}, [state, settingsQuery.data])

	const contextValue = useMemo(
		() => ({
			agent,
			avaliableAgents: settingsQuery.data?.agents || [],
			isBusy: sendMutation.isPending,
			messages: state.messages,
			setAgent,
			sendMessage,
			resetConversation,
			messageReaction,
		}),
		[
			agent,
			settingsQuery.data?.agents,
			sendMutation.isPending,
			state.messages,
			setAgent,
			sendMessage,
			resetConversation,
			messageReaction,
		]
	)

	return <AssistantContext value={contextValue}>{children}</AssistantContext>
}
const getRoutePath = (
	location: Location,
	params: Record<string, string>
): string => {
	const { pathname } = location

	if (!Object.keys(params).length) {
		return pathname // we don't need to replace anything
	}

	return Object.entries(params).reduce(
		(path: string, [paramName, paramValue]) => {
			if (paramValue) {
				return path.replace(paramValue, `:${paramName}`)
			}

			return path
		},
		pathname
	)
}
const reducer = (state: State, action: Action): State => {
	const { type } = action
	switch (type) {
		case 'set_agent':
			return {
				...state,
				agent_url: action.url,
				session_id: uuid(),
				messages: [],
			}
		case 'add':
			return { ...state, messages: [...state.messages, action.message] }
		case 'reset':
			return { ...state, session_id: uuid(), messages: [] }
		case 'reaction':
			return {
				...state,
				messages: state.messages.map((message) => {
					if (message.id === action.id) {
						return { ...message, reaction: action.reaction }
					}

					return message
				}),
			}
		default:
			return state
	}
}
type State = {
	agent_url: string | undefined
	session_id: string
	messages: Message[]
}
